import { defineStore } from 'pinia'
import { ON_COMPLETE_REDIRECT_TO_UPGRADE_PAGE } from '@js/App/SearchProfile'
import { usePage } from '@inertiajs/vue3'

// search_profiles.*_importance — must match App\Support\Enums\WishImportance.
export const MUST_HAVE = 'must_have'
export const NICE_TO_HAVE = 'nice_to_have'
export const DOESNT_MATTER = 'doesnt_matter'

// A wish is "on" for the checkbox at any level above doesnt_matter.
const isWished = importance => Boolean(importance) && importance !== DOESNT_MATTER

/**
 * The level to persist for one wish checkbox.
 *
 * A box that was already on keeps whatever level it had: plenty of profiles sit
 * at nice_to_have, and blindly upgrading them to must_have would narrow their
 * matching and quietly cost those users alerts.
 *
 * A box the user just ticked has no stored level yet, so it derives from the
 * "notify me when the requirements aren't clear" checkbox — the same mapping
 * SearchProfileHouseMatcher::deriveImportance applies server-side, which is what
 * this form's ticks used to mean before the boolean columns went stale.
 */
const wishImportanceToSend = (isTicked, storedImportance, notifyWhenRequirementsNotClear) => {
  if (!isTicked) {
    return DOESNT_MATTER
  }

  if (isWished(storedImportance)) {
    return storedImportance
  }

  return notifyWhenRequirementsNotClear ? NICE_TO_HAVE : MUST_HAVE
}

// Note: this store is manually cleared on an Inertia page load in storesSetup.js
export const useSearchProfileStore = defineStore('searchProfile', {
  state: () => {
    const page = usePage()
    const selectedCity = page.props.selected_city
    const selectedCityHasNeighbourhoods = selectedCity && selectedCity?.neighbourhoods?.length > 0

    return {
      step: 1,
      shouldResetOnNavigation: true,
      searchProfile: {
        serverId: null,

        city: selectedCity
          ? {
              ...selectedCity,
              hasNeighbourhoods: selectedCityHasNeighbourhoods
            }
          : null,
        neighbourhoods: [],
        hoveredNeighbourhood: [],
        locationSelector: selectedCityHasNeighbourhoods ? 'neighbourhoods' : 'distance',
        distance: 10,
        minRent: 0,
        maxRent: 2000,
        minBedrooms: 1,
        minSurface: 0,
        furnished: 'everything',
        hasGarden: false,
        hasBalcony: false,
        hasDakterras: false,
        hasEnergyLabelCOrBetter: false,
        hasBath: false,
        // The level behind each checkbox above. Carried through the store so a
        // save can re-send the stored level instead of rewriting it — see
        // wishImportanceToSend().
        hasGardenImportance: DOESNT_MATTER,
        hasBalconyImportance: DOESNT_MATTER,
        hasDakterrasImportance: DOESNT_MATTER,
        hasEnergyLabelCOrBetterImportance: DOESNT_MATTER,
        hasBathImportance: DOESNT_MATTER,
        isForPets: false,
        isForBalcony: false,
        isForSharing: false,
        includeWoningcorporaties: true,
        includePaidWebsites: false,
        includeSocialeLootwoningen: false,
        includeRooms: false,
        sharing3OrMore: false,
        isForStudents: false,
        notifyWhenRequirementsNotClear: true,
        sendMoreExpensiveHouses: false,
        houseRequirementsOperator: 'or',

        polygonGeojson: null,
        travelDestinationLat: null,
        travelDestinationLng: null,
        travelMinutes: null,
        travelProfile: null,

        rentbirdPlusEmail: '',
        rentbirdPlusSearchHomeFrom: null,
        rentbirdPlusGoingToLiveWith: null,
        rentbirdPlusCurrentJob: null,
        rentbirdPlusIncome: null,
        rentbirdPlusPartnerCurrentJob: null,
        rentbirdPlusPartnerIncome: null,
        rentbirdPlusGarantsteller: false,
        rentbirdPlusGarantstellerIncome: null,
        rentbirdPlusIHaveMoreThan50K: false,
        rentbirdPlusFirstname: '',
        rentbirdPlusLastname: '',
        rentbirdPlusPhone: '',
        rentbirdPlusCity: '',
        rentbirdPlusCountry: 'NL',
        rentbirdPlusLanguage: 'nl',
        rentbirdPlusHowToReachMe: '',
        rentbirdPlusProposition: 'bezichtigingsgarantie',
        rentbirdPlusPrice: 1295,
        rentbirdPlusMinimumRent: 0,
        rentbirdPlusSalaryBasedMax: 0,

        rentbirdRelocationEmail: '',
        rentbirdRelocationFlexible: false,
        rentbirdRelocationInterestedInBuying: false,
        rentbirdRelocationSearchHomeFrom: 'now',
        rentbirdRelocationVisa: 'yes',
        rentbirdRelocationVisaService: false,
        rentbirdRelocationGoingToLiveWith: 'alone',
        rentbirdRelocationCurrentJob: 'loondienst',
        rentbirdRelocationIncome: null,
        rentbirdRelocationPartnerCurrentJob: 'loondienst',
        rentbirdRelocationPartnerIncome: null,
        rentbirdRelocationFirstname: '',
        rentbirdRelocationLastname: '',
        rentbirdRelocationPhone: '',
        rentbirdRelocationNationality: 'Dutch',
        rentbirdRelocationNationalityCurrentlyLivingIn: true,
        rentbirdRelocationHowToReachMe: ''
      },
      allNeighbourhoodsOfCity: selectedCityHasNeighbourhoods ? selectedCity.neighbourhoods : [],
      actionAfterCompletion: ON_COMPLETE_REDIRECT_TO_UPGRADE_PAGE,
      matchesPerWeek: {
        isLoading: true,
        matchesPerWeek: null
      }
    }
  },

  actions: {
    setSearchProfile (searchProfile) {
      this.searchProfile = searchProfile

      if (this.searchProfile.locationSelector === 'neighbourhoods' && !this.searchProfile.city?.hasNeighbourhoods) {
        this.searchProfile.locationSelector = 'distance'
        this.searchProfile.neighbourhoods = []
      }
    },

    updateSearchProfile (newAttributes) {
      for (const [key, value] of Object.entries(newAttributes)) {
        this.searchProfile[key] = value
      }

      if ((newAttributes.city !== undefined || newAttributes.locationSelector !== undefined) &&
        this.searchProfile.locationSelector === 'neighbourhoods' &&
        !this.searchProfile.city?.hasNeighbourhoods
      ) {
        this.searchProfile.locationSelector = 'distance'
        this.searchProfile.neighbourhoods = []
      }
    },

    setSearchProfileFromDtoObject (searchProfileDto) {
      this.setSearchProfile({
        ...this.searchProfile,
        ...getSearchProfileFromDto(searchProfileDto)
      })

      if (searchProfileDto.all_neighbourhoods_of_city_without_coordinates) {
        this.allNeighbourhoodsOfCity = searchProfileDto.all_neighbourhoods_of_city_without_coordinates
      }
    }
  }
})

export const getSearchProfileFromDto = (searchProfileDto) => {
  return {
    serverId: searchProfileDto.random_id,
    status: searchProfileDto.status,
    city: {
      id: searchProfileDto.city_with_coordinates?.id,
      name: searchProfileDto.city_with_coordinates?.name,
      hasNeighbourhoods: (searchProfileDto.city_with_coordinates?.neighbourhoods || []).length > 0,
      coordinates: searchProfileDto.city_with_coordinates?.coordinates
    },
    neighbourhoods: searchProfileDto.neighbourhoods,
    name: searchProfileDto.name,
    locationSelector: searchProfileDto.location_selector,
    distance: searchProfileDto.distance,
    minRent: searchProfileDto.min_rent,
    maxRent: searchProfileDto.max_rent,
    minBedrooms: searchProfileDto.min_bedrooms,
    minSurface: searchProfileDto.min_surface,
    furnished: searchProfileDto.furnished,
    // Never read the has_* booleans: those columns are frozen (dropped from the
    // model's $fillable when the levels replaced them), so they are stale for
    // every profile saved since. The level is the truth on both sides.
    hasGarden: isWished(searchProfileDto.has_garden_importance),
    hasEnergyLabelCOrBetter: isWished(searchProfileDto.has_energy_label_c_or_better_importance),
    hasBalcony: isWished(searchProfileDto.has_balcony_importance),
    hasDakterras: isWished(searchProfileDto.has_dakterras_importance),
    hasBath: isWished(searchProfileDto.has_bath_importance),
    hasGardenImportance: searchProfileDto.has_garden_importance || DOESNT_MATTER,
    hasEnergyLabelCOrBetterImportance: searchProfileDto.has_energy_label_c_or_better_importance || DOESNT_MATTER,
    hasBalconyImportance: searchProfileDto.has_balcony_importance || DOESNT_MATTER,
    hasDakterrasImportance: searchProfileDto.has_dakterras_importance || DOESNT_MATTER,
    hasBathImportance: searchProfileDto.has_bath_importance || DOESNT_MATTER,
    isForPets: searchProfileDto.is_for_pets,
    isForBalcony: searchProfileDto.is_for_balcony,
    isForSharing: searchProfileDto.is_for_sharing,
    includeWoningcorporaties: searchProfileDto.include_woningcorporaties,
    includePaidWebsites: searchProfileDto.include_paid_websites,
    includeSocialeLootwoningen: searchProfileDto.include_sociale_lootwoningen,
    includeRooms: searchProfileDto.include_rooms,
    sharing3OrMore: searchProfileDto.sharing_3_or_more,
    isForStudents: searchProfileDto.is_for_students,
    notifyWhenRequirementsNotClear: searchProfileDto.notify_when_requirements_not_clear,
    sendMoreExpensiveHouses: searchProfileDto.send_more_expensive_houses,
    houseRequirementsOperator: searchProfileDto.house_requirements_operator,
    polygonGeojson: searchProfileDto.polygon_geojson || null,
    travelDestinationLat: searchProfileDto.travel_destination_lat || null,
    travelDestinationLng: searchProfileDto.travel_destination_lng || null,
    travelMinutes: searchProfileDto.travel_minutes || null,
    travelProfile: searchProfileDto.travel_profile || null
  }
}

export const getSearchProfileDto = searchProfile => ({
  random_id: searchProfile?.serverId,
  city: searchProfile?.city?.id || null,
  name: searchProfile.name,
  neighbourhoods: searchProfile?.neighbourhoods || [],
  location_selector: searchProfile?.locationSelector || 'distance',
  distance: searchProfile?.distance || 10,
  min_rent: searchProfile?.minRent,
  max_rent: searchProfile?.maxRent,
  min_bedrooms: searchProfile?.minBedrooms,
  min_surface: searchProfile?.minSurface,
  furnished: searchProfile?.furnished,
  has_garden_importance: wishImportanceToSend(searchProfile?.hasGarden, searchProfile?.hasGardenImportance, searchProfile?.notifyWhenRequirementsNotClear),
  has_energy_label_c_or_better_importance: wishImportanceToSend(searchProfile?.hasEnergyLabelCOrBetter, searchProfile?.hasEnergyLabelCOrBetterImportance, searchProfile?.notifyWhenRequirementsNotClear),
  has_balcony_importance: wishImportanceToSend(searchProfile?.hasBalcony, searchProfile?.hasBalconyImportance, searchProfile?.notifyWhenRequirementsNotClear),
  has_dakterras_importance: wishImportanceToSend(searchProfile?.hasDakterras, searchProfile?.hasDakterrasImportance, searchProfile?.notifyWhenRequirementsNotClear),
  has_bath_importance: wishImportanceToSend(searchProfile?.hasBath, searchProfile?.hasBathImportance, searchProfile?.notifyWhenRequirementsNotClear),
  is_for_pets: searchProfile?.isForPets,
  is_for_balcony: searchProfile?.isForBalcony,
  is_for_sharing: searchProfile?.isForSharing,
  include_woningcorporaties: searchProfile?.includeWoningcorporaties,
  include_paid_websites: searchProfile?.includePaidWebsites,
  include_sociale_lootwoningen: searchProfile?.includeSocialeLootwoningen,
  include_rooms: searchProfile?.includeRooms,
  sharing_3_or_more: searchProfile?.sharing3OrMore,
  is_for_students: searchProfile?.isForStudents,
  notify_when_requirements_not_clear: searchProfile?.notifyWhenRequirementsNotClear,
  send_more_expensive_houses: searchProfile?.sendMoreExpensiveHouses,
  house_requirements_operator: searchProfile?.houseRequirementsOperator,
  polygon_geojson: searchProfile?.polygonGeojson || null,
  travel_destination_lat: searchProfile?.travelDestinationLat || null,
  travel_destination_lng: searchProfile?.travelDestinationLng || null,
  travel_minutes: searchProfile?.travelMinutes || null,
  travel_profile: searchProfile?.travelProfile || null
})
